import { apiService as mockApi } from '@/api';
import { useState } from 'react';
import { cn } from '@/lib/utils';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Tabs } from '@/components/ui/Tabs';
import { Modal, ConfirmDialog } from '@/components/ui/Modal';
import { toast } from '@/components/ui/Toast';
import { useAppStore } from '@/store/appStore';
import {
  Settings as SettingsIcon,
  Shield,
  Bell,
  Palette,
  Cpu,
  Layers,
  Save,
  RotateCcw,
  Check,
  Globe,
  Lock,
  Mail,
  Slack,
  MessageSquare,
  Webhook,
  Key,
  Server,
  Monitor,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  RefreshCw,
  Terminal,
} from 'lucide-react';

const PRESET_ACCENTS = [
  { name: 'Insight Blue', hex: '#3b82f6' },
  { name: 'Emerald Green', hex: '#10b981' },
  { name: 'Violet Cyber', hex: '#8b5cf6' },
  { name: 'Sunset Orange', hex: '#f97316' },
  { name: 'Rose Red', hex: '#f43f5e' },
  { name: 'Cyan Neon', hex: '#06b6d4' },
];

export function Settings() {
  const [activeTab, setActiveTab] = useState('general');
  const [saving, setSaving] = useState(false);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);

  // App Store integration
  const density = useAppStore((s) => s.density);
  const setDensity = useAppStore((s) => s.setDensity);
  const accentColor = useAppStore((s) => s.accentColor);
  const setAccentColor = useAppStore((s) => s.setAccentColor);
  const sidebarCollapsed = useAppStore((s) => s.sidebarCollapsed);
  const setSidebarCollapsed = useAppStore((s) => s.setSidebarCollapsed);

  // General Settings State
  const [general, setGeneral] = useState({
    orgName: 'InsightNode Global',
    tenantName: 'tenant-default',
    timezone: 'UTC',
    dateFormat: 'MMM d, yyyy HH:mm',
    pageSize: '25',
    supportEmail: 'support@insightnode.io',
    language: 'en-US',
  });

  // Security Settings State
  const [security, setSecurity] = useState({
    mfaRequired: true,
    sessionTimeoutMin: 30,
    passwordMinLength: 12,
    passwordRequireUppercase: true,
    passwordRequireNumbers: true,
    passwordRequireSymbols: true,
    loginMaxAttempts: 5,
    ipAllowlist: '10.0.0.0/8\n192.168.1.0/24',
    corsOrigins: 'https://*.insightnode.io\nhttp://localhost:5173',
  });

  // Notifications Settings State
  const [notifications, setNotifications] = useState({
    emailCriticalAlerts: true,
    emailDeviceOffline: true,
    emailScriptFailure: true,
    emailPatchFailure: true,
    emailAgentOffline: false,
    webhookUrl: 'https://hooks.insightnode.io/v1/alerts',
    slackEnabled: true,
    slackWebhook: 'https://hooks.slack.com/services/T00/B00/XXXX',
  });

  // Agent Settings State
  const [agent, setAgent] = useState({
    version: 'v2.4.12-stable',
    heartbeatSec: 30,
    autoUpdate: true,
    updateChannel: 'stable' as 'stable' | 'beta' | 'nightly',
    offlineThresholdMin: 10,
    telemetryEnabled: true,
  });

  // Integrations State
  const [integrations, setIntegrations] = useState([
    { id: 'slack', name: 'Slack Alerts', status: 'connected', description: 'Receive real-time critical alert webhooks in Slack channels' },
    { id: 'teams', name: 'Microsoft Teams', status: 'disconnected', description: 'Post incidents and job status summaries to MS Teams' },
    { id: 'smtp', name: 'SMTP Mail Relay', status: 'connected', description: 'Custom mail transport server for email notifications' },
    { id: 'webhook', name: 'Custom Webhook API', status: 'connected', description: 'Dispatch event payloads to external REST webhooks' },
  ]);

  const handleSaveTab = async () => {
    setSaving(true);
    try {
      const sectionData = activeTab === 'general' ? general :
        activeTab === 'security' ? security :
        activeTab === 'notifications' ? notifications :
        activeTab === 'agent' ? agent : {};
      await mockApi.saveSettings(activeTab, sectionData);
      toast(`Settings saved for ${activeTab.toUpperCase()} section`);
    } catch (err) {
      toast('Failed to save settings', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleResetDefaults = () => {
    setGeneral({
      orgName: 'InsightNode Global',
      tenantName: 'tenant-default',
      timezone: 'UTC',
      dateFormat: 'MMM d, yyyy HH:mm',
      pageSize: '25',
      supportEmail: 'support@insightnode.io',
      language: 'en-US',
    });
    setSecurity({
      mfaRequired: true,
      sessionTimeoutMin: 30,
      passwordMinLength: 12,
      passwordRequireUppercase: true,
      passwordRequireNumbers: true,
      passwordRequireSymbols: true,
      loginMaxAttempts: 5,
      ipAllowlist: '',
      corsOrigins: '',
    });
    setAccentColor('#3b82f6');
    setDensity('comfortable');
    toast('Settings reset to platform defaults');
  };

  return (
    <div className="p-6 space-y-6 max-w-[1600px] mx-auto animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-border pb-4">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-xl font-bold text-text-primary tracking-tight">System Settings</h1>
            <Badge variant="default" className="text-xs font-mono">
              Tenant: {general.tenantName}
            </Badge>
          </div>
          <p className="text-xs text-text-muted mt-1">
            Global tenant policies, security enforcement, notifications, agent behavior, and app branding.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <Button variant="ghost" onClick={() => setResetDialogOpen(true)} className="gap-1.5 text-text-muted hover:text-text-primary">
            <RotateCcw className="h-3.5 w-3.5" />
            Reset Defaults
          </Button>

          <Button variant="primary" loading={saving} onClick={handleSaveTab} className="gap-2">
            <Save className="h-4 w-4" />
            Save Changes
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs
        tabs={[
          { id: 'general', label: 'General' },
          { id: 'security', label: 'Security & Auth' },
          { id: 'notifications', label: 'Notifications' },
          { id: 'appearance', label: 'Appearance & UI' },
          { id: 'agent', label: 'Agent Configuration' },
          { id: 'integrations', label: 'Integrations' },
        ]}
        defaultActive="general"
        onChange={(id) => setActiveTab(id)}
      />

      {/* ==================== 1. GENERAL TAB ==================== */}
      {activeTab === 'general' && (
        <Card className="p-6 max-w-3xl space-y-6">
          <h2 className="text-sm font-bold text-text-primary uppercase tracking-wider flex items-center gap-2">
            <Globe className="h-4 w-4 text-accent" />
            Organization & Preferences
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-text-secondary mb-1">Organization Name</label>
              <input
                type="text"
                value={general.orgName}
                onChange={(e) => setGeneral({ ...general, orgName: e.target.value })}
                className="input-base"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-text-secondary mb-1">Tenant ID</label>
              <input
                type="text"
                disabled
                value={general.tenantName}
                className="input-base opacity-60 cursor-not-allowed bg-base-elevated"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-text-secondary mb-1">System Timezone</label>
              <select
                value={general.timezone}
                onChange={(e) => setGeneral({ ...general, timezone: e.target.value })}
                className="input-base"
              >
                <option value="UTC">UTC (Coordinated Universal Time)</option>
                <option value="America/New_York">America/New_York (EST)</option>
                <option value="Europe/London">Europe/London (GMT)</option>
                <option value="Asia/Tokyo">Asia/Tokyo (JST)</option>
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-text-secondary mb-1">Date Display Format</label>
              <select
                value={general.dateFormat}
                onChange={(e) => setGeneral({ ...general, dateFormat: e.target.value })}
                className="input-base"
              >
                <option value="MMM d, yyyy HH:mm">MMM d, yyyy HH:mm (Aug 20, 2026 14:00)</option>
                <option value="YYYY-MM-DD HH:mm:ss">YYYY-MM-DD HH:mm:ss (2026-08-20 14:00:00)</option>
                <option value="MM/DD/YYYY">MM/DD/YYYY (08/20/2026)</option>
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-text-secondary mb-1">Default Table Page Size</label>
              <select
                value={general.pageSize}
                onChange={(e) => setGeneral({ ...general, pageSize: e.target.value })}
                className="input-base"
              >
                <option value="10">10 items</option>
                <option value="25">25 items</option>
                <option value="50">50 items</option>
                <option value="100">100 items</option>
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-text-secondary mb-1">Support Contact Email</label>
              <input
                type="email"
                value={general.supportEmail}
                onChange={(e) => setGeneral({ ...general, supportEmail: e.target.value })}
                className="input-base"
              />
            </div>
          </div>
        </Card>
      )}

      {/* ==================== 2. SECURITY TAB ==================== */}
      {activeTab === 'security' && (
        <Card className="p-6 max-w-3xl space-y-6">
          <h2 className="text-sm font-bold text-text-primary uppercase tracking-wider flex items-center gap-2">
            <Lock className="h-4 w-4 text-accent" />
            Security Policies & Authentication
          </h2>

          <div className="space-y-4 divide-y divide-border">
            {/* MFA Enforcement */}
            <div className="flex items-center justify-between pt-2">
              <div>
                <div className="font-semibold text-text-primary text-xs">Enforce Mandatory MFA</div>
                <div className="text-2xs text-text-muted">Require multi-factor authentication for all technician accounts.</div>
              </div>
              <input
                type="checkbox"
                checked={security.mfaRequired}
                onChange={(e) => setSecurity({ ...security, mfaRequired: e.target.checked })}
                className="h-4 w-4 rounded border-border text-accent focus:ring-accent"
              />
            </div>

            {/* Session Timeout */}
            <div className="grid grid-cols-2 gap-4 pt-4">
              <div>
                <label className="block text-xs font-medium text-text-secondary mb-1">Session Inactivity Timeout (minutes)</label>
                <input
                  type="number"
                  value={security.sessionTimeoutMin}
                  onChange={(e) => setSecurity({ ...security, sessionTimeoutMin: parseInt(e.target.value) || 15 })}
                  className="input-base"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-text-secondary mb-1">Max Failed Login Attempts</label>
                <input
                  type="number"
                  value={security.loginMaxAttempts}
                  onChange={(e) => setSecurity({ ...security, loginMaxAttempts: parseInt(e.target.value) || 5 })}
                  className="input-base"
                />
              </div>
            </div>

            {/* Password Policy */}
            <div className="pt-4 space-y-3">
              <label className="block text-xs font-bold text-text-primary uppercase">Password Requirements</label>

              <div className="grid grid-cols-2 gap-3 text-xs">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={security.passwordRequireUppercase}
                    onChange={(e) => setSecurity({ ...security, passwordRequireUppercase: e.target.checked })}
                    className="rounded border-border text-accent h-4 w-4"
                  />
                  <span>Require Uppercase Letters</span>
                </label>

                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={security.passwordRequireNumbers}
                    onChange={(e) => setSecurity({ ...security, passwordRequireNumbers: e.target.checked })}
                    className="rounded border-border text-accent h-4 w-4"
                  />
                  <span>Require Digits & Numbers</span>
                </label>

                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={security.passwordRequireSymbols}
                    onChange={(e) => setSecurity({ ...security, passwordRequireSymbols: e.target.checked })}
                    className="rounded border-border text-accent h-4 w-4"
                  />
                  <span>Require Special Symbols (!@#$)</span>
                </label>
              </div>
            </div>

            {/* IP Allowlist */}
            <div className="pt-4 space-y-2">
              <label className="block text-xs font-medium text-text-secondary">IP CIDR Allowlist (one per line)</label>
              <textarea
                value={security.ipAllowlist}
                onChange={(e) => setSecurity({ ...security, ipAllowlist: e.target.value })}
                placeholder="10.0.0.0/8"
                className="input-base font-mono text-xs h-20"
              />
            </div>
          </div>
        </Card>
      )}

      {/* ==================== 3. NOTIFICATIONS TAB ==================== */}
      {activeTab === 'notifications' && (
        <Card className="p-6 max-w-3xl space-y-6">
          <h2 className="text-sm font-bold text-text-primary uppercase tracking-wider flex items-center gap-2">
            <Bell className="h-4 w-4 text-accent" />
            Alert Dispatch & Email Triggers
          </h2>

          <div className="space-y-4">
            <div className="surface-elevated p-4 rounded-lg space-y-3">
              <span className="text-xs font-bold text-text-primary uppercase block">Email Dispatch Rules</span>

              <label className="flex items-center justify-between text-xs cursor-pointer">
                <span>Notify on Critical Alerts</span>
                <input
                  type="checkbox"
                  checked={notifications.emailCriticalAlerts}
                  onChange={(e) => setNotifications({ ...notifications, emailCriticalAlerts: e.target.checked })}
                  className="rounded text-accent h-4 w-4"
                />
              </label>

              <label className="flex items-center justify-between text-xs cursor-pointer">
                <span>Notify when device goes offline</span>
                <input
                  type="checkbox"
                  checked={notifications.emailDeviceOffline}
                  onChange={(e) => setNotifications({ ...notifications, emailDeviceOffline: e.target.checked })}
                  className="rounded text-accent h-4 w-4"
                />
              </label>

              <label className="flex items-center justify-between text-xs cursor-pointer">
                <span>Notify on Script Execution Failures</span>
                <input
                  type="checkbox"
                  checked={notifications.emailScriptFailure}
                  onChange={(e) => setNotifications({ ...notifications, emailScriptFailure: e.target.checked })}
                  className="rounded text-accent h-4 w-4"
                />
              </label>
            </div>

            <div>
              <label className="block text-xs font-medium text-text-secondary mb-1">Webhook Dispatch URL</label>
              <input
                type="text"
                value={notifications.webhookUrl}
                onChange={(e) => setNotifications({ ...notifications, webhookUrl: e.target.value })}
                className="input-base font-mono text-xs"
              />
            </div>
          </div>
        </Card>
      )}

      {/* ==================== 4. APPEARANCE TAB ==================== */}
      {activeTab === 'appearance' && (
        <Card className="p-6 max-w-3xl space-y-6">
          <h2 className="text-sm font-bold text-text-primary uppercase tracking-wider flex items-center gap-2">
            <Palette className="h-4 w-4 text-accent" />
            Theme & Accent Branding
          </h2>

          <div className="space-y-6">
            {/* Accent Pickers */}
            <div>
              <label className="block text-xs font-medium text-text-secondary mb-2">Accent Theme Color</label>
              <div className="flex flex-wrap items-center gap-3">
                {PRESET_ACCENTS.map((preset) => (
                  <button
                    key={preset.hex}
                    onClick={() => setAccentColor(preset.hex)}
                    className={cn(
                      'flex items-center gap-2 px-3 py-1.5 rounded-lg border text-xs font-medium transition-all',
                      accentColor === preset.hex ? 'border-text-primary bg-base-elevated' : 'border-border'
                    )}
                  >
                    <span className="h-3.5 w-3.5 rounded-full" style={{ backgroundColor: preset.hex }} />
                    <span>{preset.name}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Density Selector */}
            <div className="pt-4 border-t border-border flex items-center justify-between">
              <div>
                <div className="font-semibold text-text-primary text-xs">Table & Layout Density</div>
                <div className="text-2xs text-text-muted">Adjust padding and row heights across all platform tables.</div>
              </div>

              <div className="flex items-center gap-2 bg-base-elevated p-1 rounded-lg border border-border">
                <button
                  onClick={() => setDensity('comfortable')}
                  className={cn('px-3 py-1 text-xs rounded font-medium', density === 'comfortable' && 'bg-accent text-accent-fg')}
                >
                  Comfortable
                </button>
                <button
                  onClick={() => setDensity('compact')}
                  className={cn('px-3 py-1 text-xs rounded font-medium', density === 'compact' && 'bg-accent text-accent-fg')}
                >
                  Compact
                </button>
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* ==================== 5. AGENT TAB ==================== */}
      {activeTab === 'agent' && (
        <Card className="p-6 max-w-3xl space-y-6">
          <h2 className="text-sm font-bold text-text-primary uppercase tracking-wider flex items-center gap-2">
            <Cpu className="h-4 w-4 text-accent" />
            InsightNode Agent Settings
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-text-secondary mb-1">Current Agent Version</label>
              <input type="text" disabled value={agent.version} className="input-base font-mono opacity-60" />
            </div>

            <div>
              <label className="block text-xs font-medium text-text-secondary mb-1">Heartbeat Interval (seconds)</label>
              <input
                type="number"
                value={agent.heartbeatSec}
                onChange={(e) => setAgent({ ...agent, heartbeatSec: parseInt(e.target.value) || 30 })}
                className="input-base"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-text-secondary mb-1">Update Channel</label>
              <select
                value={agent.updateChannel}
                onChange={(e) => setAgent({ ...agent, updateChannel: e.target.value as any })}
                className="input-base"
              >
                <option value="stable">Stable (Recommended)</option>
                <option value="beta">Beta Release</option>
                <option value="nightly">Nightly Build</option>
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-text-secondary mb-1">Offline Marking Threshold (minutes)</label>
              <input
                type="number"
                value={agent.offlineThresholdMin}
                onChange={(e) => setAgent({ ...agent, offlineThresholdMin: parseInt(e.target.value) || 10 })}
                className="input-base"
              />
            </div>
          </div>
        </Card>
      )}

      {/* ==================== 6. INTEGRATIONS TAB ==================== */}
      {activeTab === 'integrations' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-4xl">
          {integrations.map((item) => (
            <Card key={item.id} className="p-4 space-y-3 flex flex-col justify-between">
              <div>
                <div className="flex items-center justify-between">
                  <h3 className="font-bold text-text-primary text-sm">{item.name}</h3>
                  <Badge variant={item.status === 'connected' ? 'online' : 'default'} dot>
                    <span className="capitalize">{item.status}</span>
                  </Badge>
                </div>
                <p className="text-xs text-text-muted mt-2">{item.description}</p>
              </div>

              <div className="pt-3 border-t border-border flex justify-end">
                <Button
                  variant={item.status === 'connected' ? 'secondary' : 'primary'}
                  size="sm"
                  onClick={() => {
                    const next = item.status === 'connected' ? 'disconnected' : 'connected';
                    setIntegrations(integrations.map((i) => (i.id === item.id ? { ...i, status: next } : i)));
                    toast(`${item.name} ${next}`);
                  }}
                >
                  {item.status === 'connected' ? 'Disconnect' : 'Connect'}
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Reset Confirmation Dialog */}
      <ConfirmDialog
        open={resetDialogOpen}
        onClose={() => setResetDialogOpen(false)}
        onConfirm={handleResetDefaults}
        title="Reset Settings to Platform Defaults"
        message="Are you sure? This will restore default session timeouts, password rules, and default accent theme."
        danger
      />
    </div>
  );
}
